import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Form, FormInput } from "@/components/ui/form"
import { Input, MaskedInput } from "@/components/ui/input"
import { useFormState } from "@/hooks/use-form-state"
import useInputCurrencyFormat from "@/hooks/use-input-currency-format"
import {
  accountFormSchema,
  createAccount,
  type TAccount,
  updateAccount,
} from "@/http/finance"

type TAccountFormProps = {
  account?: TAccount
  organizationSlug: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export function AccountForm({
  account,
  organizationSlug,
  open,
  onOpenChange,
  onSuccess,
}: TAccountFormProps) {
  const isEditing = !!account
  const currencyFormatter = useInputCurrencyFormat()

  const { state, ...form } = useFormState({
    action: async (data) => {
      if (isEditing && account) {
        const { error } = await updateAccount({
          accountId: account.id,
          organizationSlug,
          name: data.name,
        })

        if (error) {
          return {
            success: false,
            message: error.message || "Erro ao atualizar conta",
          }
        }

        return {
          success: true,
          message: "Conta atualizada com sucesso",
        }
      }

      const payload = {
        name: data.name,
        organizationSlug,
        initialBalance: data.initialBalance,
      }

      const { error } = await createAccount(payload)

      if (error) {
        return {
          success: false,
          message: error.message || "Erro ao criar conta",
        }
      }

      return {
        success: true,
        message: "Conta criada com sucesso",
      }
    },
    zodSchema: accountFormSchema,
    defaultValues: {
      name: account?.name || "",
      organizationSlug,
      initialBalance: 0,
    },
    onSuccess: () => {
      onSuccess?.()
      onOpenChange(false)
      form.reset()
    },
    onFailure(values, error) {
      console.error("Account form submission failed:", { values, error })
      toast.error(error || "Erro ao salvar conta")
    },
  })

  const handleCancel = () => {
    onOpenChange(false)
    form.reset()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? "Editar Conta" : "Nova Conta"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Edite as informações da conta financeira"
              : "Crie uma nova conta financeira para sua organização"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="grid gap-4 py-4">
            <FormInput name="name" label="Nome da Conta">
              <Input placeholder="Ex: Caixa, Banco..." />
            </FormInput>

            {!isEditing && (
              <FormInput name="initialBalance" label="Saldo Inicial (opcional)">
                <MaskedInput
                  type="tel"
                  valueIsNumericString
                  format={currencyFormatter}
                  data-testid="initial-balance"
                />
              </FormInput>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={state.isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={state.isSubmitting}>
              {state.isSubmitting ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
